import config from "@src/config";
import {
	cleanPathAndQuery,
	isKnownWebDomain,
} from "@src/utils/illumi-deep-link-path";

/**
 * Resolves the canonical public web app origin.
 * In a web browser, this defaults to `window.location.origin`.
 * In the Electron desktop app (where origin is `app://localhost`), this resolves
 * to the publicly reachable web origin (e.g. `https://proto.illumi.sh` or
 * `https://app.illumi.systems` in proto, `https://app.illumi.one` in prod) so shared
 * links and QR codes can be opened by external web recipients (fixes DF-718).
 */
export function getPublicWebAppOrigin(): string {
	const isElectron = Boolean(
		typeof window !== "undefined" && window.electronAPI?.isElectron,
	);

	if (!isElectron && typeof window !== "undefined" && window.location?.origin) {
		// In browser, use current host unless it's app:// (rare edge case)
		if (!window.location.origin.startsWith("app://")) {
			return window.location.origin;
		}
	}

	// Desktop or app:// scheme: infer from API environment
	const apiUrl = config.graphqlUrl || config.restApiUrl || "";
	if (apiUrl.includes("illumi.sh")) {
		return "https://proto.illumi.sh";
	}
	if (apiUrl.includes("illumi.systems")) {
		return "https://app.illumi.systems";
	}

	return "https://app.illumi.one";
}

export { cleanPathAndQuery } from "@src/utils/illumi-deep-link-path";

/**
 * Converts a relative path, web URL, or deep link into a custom scheme `illumi://` URL.
 * Used to offer "Open in Desktop App" actions from web or deep link navigation.
 */
export function toIllumiDeepLink(pathOrUrl: string): string {
	if (!pathOrUrl) {
		return "illumi://";
	}

	if (pathOrUrl.startsWith("illumi://")) {
		return pathOrUrl;
	}

	let targetPath = pathOrUrl;

	try {
		// If it's a full URL (http/https/app)
		if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(pathOrUrl)) {
			const parsed = new URL(pathOrUrl);
			targetPath = `${parsed.pathname}${parsed.search}${parsed.hash}`;
		}
	} catch {
		// fall through to path handling
	}

	const { cleanPath, queryHash } = cleanPathAndQuery(targetPath);

	return cleanPath
		? `illumi://${cleanPath}${queryHash}`
		: `illumi://${queryHash}`;
}

/**
 * Converts an `illumi://` deep link, `app://` URL, or relative path to a public canonical web URL.
 */
export function toPublicWebUrl(pathOrDeepLink: string): string {
	const origin = getPublicWebAppOrigin();

	if (/^https?:\/\//i.test(pathOrDeepLink)) {
		return pathOrDeepLink;
	}

	let route = pathOrDeepLink;

	if (pathOrDeepLink.startsWith("illumi://")) {
		try {
			const parsed = new URL(pathOrDeepLink);
			const host = parsed.host;
			if (isKnownWebDomain(host) || !host) {
				route = `${parsed.pathname}${parsed.search}${parsed.hash}`;
			} else {
				route = `/${host}${parsed.pathname}${parsed.search}${parsed.hash}`;
			}
		} catch {
			route = pathOrDeepLink.replace(/^illumi:\/\//, "");
		}
	} else if (pathOrDeepLink.startsWith("app://")) {
		try {
			const parsed = new URL(pathOrDeepLink);
			route = `${parsed.pathname}${parsed.search}${parsed.hash}`;
		} catch {
			route = pathOrDeepLink.replace(/^app:\/\/[^/]+/, "");
		}
	}

	const { cleanPath, queryHash } = cleanPathAndQuery(route);

	return `${origin}/${cleanPath}${queryHash}`;
}

/**
 * Detects whether the current environment is a desktop web browser
 * (i.e. running in a desktop OS browser like Chrome/Safari/Edge/Firefox,
 * NOT inside Electron, and NOT on mobile/tablet devices like iOS/Android).
 */
export function isDesktopBrowser(): boolean {
	if (typeof window === "undefined" || typeof navigator === "undefined") {
		return false;
	}

	if (window.electronAPI?.isElectron) {
		return false;
	}

	const ua = navigator.userAgent || "";
	const isMobileUa =
		/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
	const isIPad =
		/iPad/i.test(ua) ||
		(navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);

	return !isMobileUa && !isIPad;
}

/**
 * Triggers an OS-level protocol handoff to open the current or given path in the
 * Illumi desktop app via the custom `illumi://` scheme.
 */
export function openInDesktopApp(pathOrUrl?: string): void {
	if (typeof window === "undefined") {
		return;
	}

	const target =
		pathOrUrl ??
		`${window.location.pathname}${window.location.search}${window.location.hash}`;
	const deepLink = toIllumiDeepLink(target);
	window.location.href = deepLink;
}
